import React, { useMemo, useState } from 'react';

import { CFColorTokenProp } from '~/theme/tokens/contentful-color';

type MobilePhoneNumberButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    cfButtonColor?: CFColorTokenProp;
    cfButtonHoverColor?: CFColorTokenProp;
    useDropshadow?: boolean;
  };

const CF_COLORS: Record<string, string> = {
  spearmint: '#80D6B0',
  green: '#3BBFAD',
  ginger: '#F4BA42',
  blackberry: '#1D252C',
  cinnamint: '#D66755',
  sweetmint: '#035C67',
  white: '#FFFFFF',
  mutedNavy: '#9AB5B8',
  transparent: 'transparent',
  limegreen: '#BBF174',
  callout: '#CDE012'
};

function getTokenKey(
  token?: CFColorTokenProp
): keyof typeof CF_COLORS | undefined {
  if (!token) return undefined;
  if (typeof token === 'string') return token as keyof typeof CF_COLORS;
  if (typeof token === 'object') {
    const obj = token as Record<string, keyof typeof CF_COLORS | undefined>;
    return (
      obj['0'] ?? obj.min ?? obj.mobile ?? obj.tablet ?? obj.desktop ?? obj.max
    );
  }
  return undefined;
}

function resolveToken(token?: CFColorTokenProp, fallback?: string) {
  const key = getTokenKey(token);
  if (!key) return fallback;
  return CF_COLORS[key] ?? fallback;
}

function resolveTextColor(token?: CFColorTokenProp) {
  const darkTokens = new Set(['blackberry', 'sweetmint']);
  const key = getTokenKey(token);
  return key && darkTokens.has(key) ? '#FFFFFF' : '#1D252C';
}

export function MobilePhoneNumberButton({
  cfButtonColor = 'ginger',
  cfButtonHoverColor = 'green',
  useDropshadow,
  className,
  style,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  children,
  ...rest
}: MobilePhoneNumberButtonProps) {
  const [hovered, setHovered] = useState(false);

  const baseBg = useMemo(
    () => resolveToken(cfButtonColor, CF_COLORS.ginger),
    [cfButtonColor]
  );
  const hoverBg = useMemo(
    () => resolveToken(cfButtonHoverColor, baseBg),
    [cfButtonHoverColor, baseBg]
  );

  const bgColor = hovered ? hoverBg : baseBg;
  const textColor = resolveTextColor(
    hovered ? cfButtonHoverColor : cfButtonColor
  );

  return (
    <button
      {...rest}
      className={[
        'absolute',
        'box-border',
        'right-1.5',
        'h-10', // 40px default
        'small:h-10',
        'tablet:h-10',
        'text-[12px]',
        'px-3', // ~12px horizontal padding
        'rounded-[8px]',
        'font-medium',
        'transition-colors',
        'duration-200',
        'small:right-1.5',
        'small:text-[14px]',
        'small:min-w-[112px]',
        'mobile:min-w-[112px]',
        'tablet:min-w-[112px]',
        'tablet:text-[14px]',
        className || ''
      ].join(' ')}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: useDropshadow ? '8px' : '8px',
        ...style
      }}
      onMouseEnter={(e) => {
        setHovered(true);
        onMouseEnter?.(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        onMouseLeave?.(e);
      }}
      onFocus={(e) => {
        setHovered(true);
        onFocus?.(e);
      }}
      onBlur={(e) => {
        setHovered(false);
        onBlur?.(e);
      }}
    >
      {children}
    </button>
  );
}

export default MobilePhoneNumberButton;
